import { packageCredits, suggestPackages, type PaymentMethod } from '@trendator/shared';
import { useState } from 'react';
import { rub } from '../format';
import { haptic } from '../telegram';

const NOUN = { trend: 'тренд', photo: 'фото', video: 'видео', song: 'песня' } as const;
const NOUN_ACC = { trend: 'тренд', photo: 'фото', video: 'видео', song: 'песню' } as const;

const METHOD_LABEL: Record<PaymentMethod, string> = { card_sbp: 'Карта или СБП', usdt: 'Крипта · USDT' };

/**
 * «Не хватает баланса» — docs/design/LowBalance.dc.html.
 * Ручной режим — с недели 3, тренды — с недели 4. После оплаты генерация запускается сама.
 */
export function LowBalanceSheet({
  balance,
  price,
  methods,
  kind = 'trend',
  onPay,
  onClose,
}: {
  balance: number;
  price: number;
  methods: PaymentMethod[];
  /** Что покупаем: тренд или фото / видео / песню в ручном режиме. */
  kind?: 'trend' | 'photo' | 'video' | 'song';
  onPay: (packageId: string, method: PaymentMethod) => void;
  onClose: () => void;
}) {
  const options = suggestPackages(balance, price);
  const [packageId, setPackageId] = useState(options[0]!.id);
  const [methodIdx, setMethodIdx] = useState(0);
  const method = methods[methodIdx];
  const chosen = options.find((p) => p.id === packageId)!;

  const noun = NOUN[kind];
  const hint = (credits: number, first: boolean) => {
    const left = balance + credits - price;
    if (first) return `хватит на ${NOUN_ACC[kind]}, останется ${rub(left)}`;
    const more = Math.floor(left / price);
    const word =
      kind === 'trend'
        ? plural(more, 'тренд', 'тренда', 'трендов')
        : kind === 'video'
          ? plural(more, 'видео', 'видео', 'видео')
          : kind === 'song'
            ? plural(more, 'песню', 'песни', 'песен')
            : 'фото';
    return `придёт ${credits.toLocaleString('ru-RU')}${more > 0 ? ` — ещё на ${more} ${word}` : ''}`;
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-label="Не хватает баланса" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__grip" />
        <div className="sheet__head">
          <h2 className="sheet__title">Не хватает {rub(price - balance)}</h2>
          <p className="sheet__text">
            На балансе {rub(balance)}, {noun} стоит {rub(price)}
          </p>
        </div>

        <div className="sheet__options">
          {options.map((p, i) => (
            <label key={p.id} className={`sheet-option${p.id === packageId ? ' sheet-option--on' : ''}`}>
              <span className="sheet-option__text">
                <span className="sheet-option__amount">
                  {rub(p.amount)}
                  {p.bonusPercent > 0 && <span className="sheet-option__bonus">+{p.bonusPercent}%</span>}
                </span>
                <span className="sheet-option__hint">{hint(packageCredits(p), i === 0)}</span>
              </span>
              <input
                type="radio"
                name="sum"
                checked={p.id === packageId}
                onChange={() => {
                  haptic('select');
                  setPackageId(p.id);
                }}
              />
            </label>
          ))}
        </div>

        {method && (
          <div className="sheet__method">
            <span className="glass-list__icon glass-list__icon--tag sheet__method-icon">{method === 'usdt' ? 'USDT' : 'СБП'}</span>
            <span style={{ flex: 1 }}>{METHOD_LABEL[method]}</span>
            {methods.length > 1 && (
              <button type="button" className="link-btn" onClick={() => setMethodIdx((i) => (i + 1) % methods.length)}>
                Сменить
              </button>
            )}
          </div>
        )}

        {!method && <p className="sheet__note">Оплата подключается — загляни чуть позже.</p>}
        <button
          type="button"
          className="btn-accent"
          style={{ marginTop: 16 }}
          disabled={!method}
          onClick={() => method && onPay(chosen.id, method)}
        >
          Оплатить {rub(chosen.amount)} и создать
        </button>
        <p className="sheet__note">После оплаты генерация запустится сама</p>
      </div>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
